<script setup>
import { ref, computed, onMounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import { useStoreGetters } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useImageZoom } from 'dashboard/composables/useImageZoom';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { downloadFile } from '@chatwoot/utils';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  allAttachments: {
    type: Array,
    required: true,
  },
  autoPlay: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'error']);
const show = defineModel('show', { type: Boolean, default: false });

const { t } = useI18n();
const getters = useStoreGetters();
const shouldLog =
  (import.meta.env?.VITE_CONSOLE_LOG ?? 'true').toString().toLowerCase() ===
  'true';
const logDebug = (...args) => {
  if (shouldLog) {
    // eslint-disable-next-line no-console
    console.log('[GalleryView]', ...args);
  }
};

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  IG_REEL: 'ig_reel',
  AUDIO: 'audio',
};

const getAttachmentId = attachment =>
  attachment?.id ||
  attachment?.data_url ||
  attachment?.dataUrl ||
  attachment?.message_id ||
  attachment?.messageId;

const getAttachmentUrl = attachment =>
  attachment?.data_url ||
  attachment?.dataUrl ||
  attachment?.thumb_url ||
  attachment?.thumbUrl ||
  '';

const normalizeType = type => {
  if (!type) return ALLOWED_FILE_TYPES.IMAGE; // assume image when missing
  const lower = type.toString().toLowerCase();
  if (lower.includes('image')) return ALLOWED_FILE_TYPES.IMAGE;
  if (lower.includes('video')) return ALLOWED_FILE_TYPES.VIDEO;
  if (lower.includes('audio')) return ALLOWED_FILE_TYPES.AUDIO;
  if (lower.includes('ig_reel')) return ALLOWED_FILE_TYPES.IG_REEL;
  return lower;
};

const attachmentTime = attachment => {
  const value = attachment.created_at || attachment.timestamp;
  if (!value) return 0;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  return Date.parse(value) || 0;
};
// Navigate forward in conversation time, regardless of the library's grid order.
const orderedAttachments = computed(() =>
  [...props.allAttachments].sort(
    (a, b) =>
      attachmentTime(a) - attachmentTime(b) ||
      (Number(a.id) || 0) - (Number(b.id) || 0)
  )
);
const initialActiveIndex = orderedAttachments.value.findIndex(
  attachment =>
    getAttachmentId(attachment) === getAttachmentId(props.attachment)
);

if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.info('[GalleryView] attachment', props.attachment);
  // eslint-disable-next-line no-console
  console.info(
    '[GalleryView] allAttachments count',
    props.allAttachments.length
  );
  // eslint-disable-next-line no-console
  console.info(
    '[GalleryView] initialActiveIndex',
    initialActiveIndex,
    'firstIds',
    {
      incoming: getAttachmentId(props.attachment),
      firstList: getAttachmentId(props.allAttachments[0]),
    }
  );
}

const isDownloading = ref(false);
const activeAttachment = ref({});
const activeFileType = ref('');
const activeImageIndex = ref(initialActiveIndex >= 0 ? initialActiveIndex : 0);
const activeAttachmentUrl = computed(() =>
  getAttachmentUrl(activeAttachment.value)
);

const imageRef = useTemplateRef('imageRef');

const {
  imageWrapperStyle,
  imageStyle,
  onRotate,
  activeImageRotation,
  onZoom,
  onDoubleClickZoomImage,
  onWheelImageZoom,
  onMouseMove,
  onMouseLeave,
  resetZoomAndRotation,
  isTouching,
  onTouchStart,
  onTouchMove,
  onTouchCancel,
} = useImageZoom(imageRef);

const currentUser = computed(() => getters.getCurrentUser.value);
const hasMoreThanOneAttachment = computed(
  () => props.allAttachments.length > 1
);

const readableTime = computed(() => {
  const { created_at: createdAt } = activeAttachment.value;
  if (!createdAt) return '';
  return messageTimestamp(createdAt, 'LLL d yyyy, h:mm a') || '';
});

const isImage = computed(
  () => activeFileType.value === ALLOWED_FILE_TYPES.IMAGE
);
const isVideo = computed(() =>
  [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
    activeFileType.value
  )
);
const isAudio = computed(
  () => activeFileType.value === ALLOWED_FILE_TYPES.AUDIO
);

const senderDetails = computed(() => {
  const {
    name,
    available_name: availableName,
    avatar_url,
    thumbnail,
    id,
  } = activeAttachment.value?.sender || props.attachment?.sender || {};

  return {
    name: currentUser.value?.id === id ? 'You' : name || availableName || '',
    avatar: thumbnail || avatar_url || '',
  };
});

const fileNameFromDataUrl = computed(() => {
  const dataUrl = getAttachmentUrl(activeAttachment.value);
  if (!dataUrl) return '';

  const fileName = dataUrl.split('/').pop();
  return fileName ? decodeURIComponent(fileName) : '';
});

const onClose = () => emit('close');

const setImageAndVideoSrc = attachment => {
  if (!attachment) return;
  const { file_type: rawType } = attachment || {};
  const url = getAttachmentUrl(attachment);
  const type = normalizeType(rawType);
  if (!type || !url) return;

  activeAttachment.value = attachment;
  activeFileType.value = type;
};

const onClickChangeAttachment = (attachment, index) => {
  if (!attachment) return;

  activeImageIndex.value = index;
  setImageAndVideoSrc(attachment);
  resetZoomAndRotation();
};

const onClickDownload = async () => {
  const {
    file_type: rawType,
    data_url: url,
    extension,
  } = activeAttachment.value;
  const type = normalizeType(rawType);
  if (!Object.values(ALLOWED_FILE_TYPES).includes(type)) return;

  try {
    isDownloading.value = true;
    await downloadFile({ url, type, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const keyboardEvents = {
  Escape: { action: onClose },
  ArrowLeft: {
    action: () => {
      const nextIndex = activeImageIndex.value - 1;
      onClickChangeAttachment(orderedAttachments.value[nextIndex], nextIndex);
    },
  },
  ArrowRight: {
    action: () => {
      const nextIndex = activeImageIndex.value + 1;
      onClickChangeAttachment(orderedAttachments.value[nextIndex], nextIndex);
    },
  },
};

useKeyboardEvents(keyboardEvents);

onMounted(() => {
  const initialAttachment =
    props.attachment ||
    props.allAttachments[initialActiveIndex >= 0 ? initialActiveIndex : 0] ||
    props.allAttachments[0];
  logDebug('mount', {
    attachment: initialAttachment,
    allAttachmentsCount: props.allAttachments.length,
  });
  setImageAndVideoSrc(initialAttachment);
  logDebug('after setImageAndVideoSrc', {
    activeAttachment: activeAttachment.value,
    activeFileType: activeFileType.value,
    activeAttachmentUrl: activeAttachmentUrl.value,
    activeImageIndex: activeImageIndex.value,
  });
});
</script>

<template>
  <TeleportWithDirection to="body">
    <woot-modal
      v-model:show="show"
      full-width
      :show-close-button="false"
      :on-close="onClose"
      :style="{ zIndex: 120 }"
    >
      <div
        class="bg-n-background flex flex-col h-[inherit] w-[inherit] overflow-hidden select-none"
        @click.self="onClose"
      >
        <header
          class="z-10 flex flex-wrap items-center justify-between gap-2 w-full shrink-0 px-2 sm:px-6 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] bg-n-background border-b border-n-weak"
          @click.stop
        >
          <div
            v-if="senderDetails"
            class="flex items-center min-w-0 flex-1 sm:flex-initial sm:max-w-60"
          >
            <Avatar
              v-if="senderDetails.avatar"
              :name="senderDetails.name"
              :src="senderDetails.avatar"
              :size="40"
              rounded-full
              class="flex-shrink-0"
            />
            <div
              class="flex flex-col min-w-0 ml-2 rtl:ml-0 rtl:mr-2 overflow-hidden"
            >
              <h3 class="text-base leading-5 m-0 font-medium">
                <span class="block truncate text-n-slate-12">
                  {{ senderDetails.name }}
                </span>
              </h3>
              <span
                class="text-xs text-n-slate-11 whitespace-nowrap text-ellipsis"
              >
                {{ readableTime }}
              </span>
            </div>
          </div>

          <div
            class="hidden lg:block flex-1 min-w-0 mx-2 px-2 truncate text-sm font-medium text-center text-n-slate-12"
          >
            <span v-dompurify-html="fileNameFromDataUrl" class="truncate" />
          </div>

          <div
            class="order-last sm:order-none flex items-center justify-center gap-2 w-full sm:w-auto shrink-0"
          >
            <NextButton
              v-if="isImage"
              icon="i-lucide-zoom-in"
              slate
              ghost
              @click="onZoom(0.1)"
            />
            <NextButton
              v-if="isImage"
              icon="i-lucide-zoom-out"
              slate
              ghost
              @click="onZoom(-0.1)"
            />
            <NextButton
              v-if="isImage"
              icon="i-lucide-rotate-ccw"
              slate
              ghost
              @click="onRotate('counter-clockwise')"
            />
            <NextButton
              v-if="isImage"
              icon="i-lucide-rotate-cw"
              slate
              ghost
              @click="onRotate('clockwise')"
            />
            <NextButton
              icon="i-lucide-download"
              slate
              ghost
              :is-loading="isDownloading"
              :disabled="isDownloading"
              @click="onClickDownload"
            />
          </div>
          <NextButton
            icon="i-lucide-x"
            :label="t('GENERAL.CLOSE')"
            :aria-label="t('GENERAL.CLOSE')"
            class="!min-h-11 shrink-0"
            slate
            faded
            @click="onClose"
          />
        </header>

        <main class="flex items-stretch flex-1 min-h-0 overflow-hidden">
          <div class="flex items-center justify-center w-11 sm:w-16 shrink-0">
            <NextButton
              v-if="hasMoreThanOneAttachment"
              icon="ltr:i-lucide-chevron-left rtl:i-lucide-chevron-right"
              class="z-10"
              blue
              faded
              lg
              :disabled="activeImageIndex === 0"
              @click.stop="
                onClickChangeAttachment(
                  orderedAttachments[activeImageIndex - 1],
                  activeImageIndex - 1
                )
              "
            />
          </div>

          <div
            class="flex-1 min-w-0 flex items-center justify-center overflow-hidden"
          >
            <div
              v-if="isImage"
              :style="imageWrapperStyle"
              class="flex items-center justify-center origin-center touch-none"
              :class="{
                // Adjust dimensions when rotated 90/270 degrees to maintain visibility
                // and prevent image from overflowing container in different aspect ratios
                'w-[calc(100dvh-8rem)] h-[calc(100dvw-7rem)]':
                  activeImageRotation % 180 !== 0,
                'size-full': activeImageRotation % 180 === 0,
              }"
              @touchstart.stop="onTouchStart"
              @touchmove.prevent.stop="onTouchMove"
              @touchend.stop="onTouchStart"
              @touchcancel.stop="onTouchCancel"
            >
              <img
                ref="imageRef"
                :key="getAttachmentId(activeAttachment) || activeImageIndex"
                :src="activeAttachmentUrl"
                :style="imageStyle"
                class="max-h-full max-w-full object-contain duration-100 ease-in-out transform select-none"
                :class="{ '!duration-0': isTouching }"
                draggable="false"
                @click.stop
                @dblclick.stop="onDoubleClickZoomImage"
                @wheel.prevent.stop="onWheelImageZoom"
                @mousemove="onMouseMove"
                @mouseleave="onMouseLeave"
              />
            </div>

            <video
              v-if="isVideo"
              :key="getAttachmentId(activeAttachment) || activeImageIndex"
              :src="activeAttachmentUrl"
              controls
              playsInline
              :autoplay="autoPlay"
              class="max-h-full max-w-full object-contain"
              @click.stop
            />

            <audio
              v-if="isAudio"
              :key="getAttachmentId(activeAttachment) || activeImageIndex"
              controls
              :autoplay="autoPlay"
              class="w-full max-w-md"
              @click.stop
            >
              <source :src="`${activeAttachmentUrl}?t=${Date.now()}`" />
            </audio>
          </div>

          <div class="flex items-center justify-center w-11 sm:w-16 shrink-0">
            <NextButton
              v-if="hasMoreThanOneAttachment"
              icon="ltr:i-lucide-chevron-right rtl:i-lucide-chevron-left"
              class="z-10"
              blue
              faded
              lg
              :disabled="activeImageIndex === allAttachments.length - 1"
              @click.stop="
                onClickChangeAttachment(
                  orderedAttachments[activeImageIndex + 1],
                  activeImageIndex + 1
                )
              "
            />
          </div>
        </main>

        <footer
          class="z-10 shrink-0 flex items-center justify-center min-h-12 pb-[env(safe-area-inset-bottom)] border-t border-n-weak"
        >
          <div
            class="rounded-md flex items-center justify-center px-3 py-1 bg-n-slate-3 text-n-slate-12 text-sm font-medium"
          >
            {{ `${activeImageIndex + 1} / ${allAttachments.length}` }}
          </div>
        </footer>
      </div>
    </woot-modal>
  </TeleportWithDirection>
</template>
